import { useRef, type CSSProperties, type ReactNode, type RefObject } from "react";
import { Trans } from "react-i18next";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { Clock, Shirt, Thermometer, TriangleAlert } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  SCENE_STAGE,
  useSceneRoom,
} from "@/features/wash-start-guide/start-guide-parts";

// A Renzacci washer drawn from photos of our machines, filmed by a "camera":
// - "door": whole machine, door slams shut on a loop: the machine shakes, PUFF!
// - "screen": camera zooms into the touchscreen while the rest of the machine
//   fades away. The zoom lands exactly on the first of the touchscreen's three
//   states, numbered under one another: SELECT A PROGRAM → program list →
//   INSERT CREDIT (the state the machine has to wait in for payment). The other
//   two fan out from behind the first one. They fill the whole room the guide
//   has, not just the stage.
// - "closed": whole machine at rest, door closed
// - "running": door closed, the drum spins up and keeps spinning
// The scene stays mounted between steps, so changing `view` animates the zoom.
// The zoom is one GSAP timeline: it plays forward into "screen" and backwards
// out of it, from wherever it is, so it can be interrupted.
// CSS 3D only: kiosk Chrome runs with --disable-gpu --disable-software-rasterizer,
// so WebGL (three.js) isn't available there.

export type WasherView = "door" | "closed" | "screen" | "running";

const STAGE = SCENE_STAGE;
const MACHINE = { left: 230, top: 40, width: 500, height: 500 };

// touchscreen glass, in machine coordinates
const SCREEN = { left: 68, top: 44, width: 100, height: 58 };

const DOOR_CENTER = { x: 250, y: 300 };
const GASKET_R = 160;
const OPENING_R = 120;
const DOOR_R = 140;
const GLASS_R = 92;

const DOOR_LOOP_SECONDS = 5;
const CAMERA_SECONDS = 1.4;
// Spins up (ease-in, so it reads as "starting") while the camera zooms out,
// then keeps turning at the speed the spin-up ended on (~1030°/s).
const DRUM_SPIN = `washer-drum-spin 3s ease-in ${CAMERA_SECONDS / 2}s both, washer-drum-steady 0.35s linear ${CAMERA_SECONDS / 2 + 3}s infinite`;

export function WasherScene({
  view,
  note,
  capacity,
}: {
  view: WasherView;
  // "screen" only: a warning to the right of the touchscreen states
  note?: ReactNode;
  // kg, shown on the info sticker like on the real machine
  capacity: number;
}) {
  const scope = useRef<HTMLDivElement>(null);
  const room = useSceneRoom();
  // the screens also use what the shorter text of their step leaves free
  const freeAbove = room.freeAbove.screen ?? 0;
  const layout = stepsLayout(room, freeAbove, !!note);
  const onScreen = view === "screen";
  useZoomTimeline(scope, layout, onScreen);
  // clip to the whole room, so the zoom can land on the first step's screen
  const clipLeft = (STAGE.width - room.width) / 2;
  return (
    <div
      ref={scope}
      className="relative"
      style={{ width: STAGE.width, height: STAGE.height }}
    >
      <WasherKeyframes />
      <div
        className="absolute overflow-hidden"
        style={{
          left: clipLeft,
          top: -freeAbove,
          width: room.width,
          height: room.height + freeAbove,
        }}
      >
        <div
          data-zoom="camera"
          className="absolute"
          style={{
            left: -clipLeft,
            top: freeAbove,
            width: STAGE.width,
            height: STAGE.height,
          }}
        >
          <div data-zoom="machine">
            {/* soft shadow so the machine stands on something */}
            <div
              className="absolute rounded-full bg-black/25 blur-md"
              style={{ left: MACHINE.left - 10, width: MACHINE.width + 20, top: MACHINE.top + MACHINE.height - 14, height: 28 }}
            />
            <Machine view={view} capacity={capacity} />
          </div>
          {/* the glass stays while the machine around it fades (hidden at
              rest, so it doesn't miss the door slam's shake) */}
          <div data-zoom="glass">
            <TouchscreenGlass
              left={MACHINE.left + SCREEN.left}
              top={MACHINE.top + SCREEN.top}
            />
          </div>
        </div>
      </div>
      <ScreenSteps layout={layout} note={note} hidden={!onScreen} />
    </div>
  );
}

type StepsLayout = ReturnType<typeof stepsLayout>;

// Runs the zoom timeline towards the current view. The room usually changes
// size right as the view does (the next step's text is shorter), which moves
// the zoom's target: then the timeline is rebuilt and carries on from where
// the old one was.
function useZoomTimeline(
  scope: RefObject<HTMLDivElement | null>,
  layout: StepsLayout,
  onScreen: boolean,
) {
  const timeline = useRef<gsap.core.Timeline>(null);
  useGSAP(
    () => {
      const old = timeline.current;
      old?.kill();
      const next = zoomTimeline(layout).progress(
        old ? old.progress() : onScreen ? 1 : 0,
      );
      // going back shouldn't keep the user waiting: twice as fast
      if (onScreen) next.timeScale(ZOOM_SPEED).play();
      else next.timeScale(ZOOM_SPEED * 2).reverse();
      timeline.current = next;
      // unmounted: the next mount starts from the view, not a dead timeline
      return () => {
        timeline.current = null;
      };
    },
    {
      scope,
      dependencies: [
        onScreen,
        layout.glass.width,
        layout.top,
        layout.left,
        !!layout.note,
      ],
    },
  );
}

// the timeline below plays this much faster than its times say
const ZOOM_SPEED = 1.4;

// Times in seconds; selectors are scoped to the scene by useGSAP.
function zoomTimeline(layout: StepsLayout) {
  const zoom = layout.glass.width / SCREEN.width;
  const timeline = gsap.timeline({ paused: true });
  // camera: the touchscreen's glass ends up exactly on the first step's glass
  timeline.fromTo(
    "[data-zoom=camera]",
    { x: 0, y: 0, scale: 1, transformOrigin: "0 0" },
    {
      x: layout.glass.left - (MACHINE.left + SCREEN.left) * zoom,
      y: layout.glass.top - (MACHINE.top + SCREEN.top) * zoom,
      scale: zoom,
      duration: CAMERA_SECONDS,
      ease: "power3.inOut",
    },
    0,
  );
  timeline.fromTo(
    "[data-zoom=glass]",
    { autoAlpha: 0 },
    { autoAlpha: 1, duration: 0.01 },
    0,
  );
  // the rest of the machine is gone by the time the camera gets there
  timeline.fromTo(
    "[data-zoom=machine]",
    { autoAlpha: 1 },
    { autoAlpha: 0, duration: 0.8, ease: "power1.inOut" },
    0.3,
  );
  // the first screen's bezel and finger come in over the zoomed glass
  timeline.fromTo(
    "[data-zoom=screen-0]",
    { autoAlpha: 0 },
    { autoAlpha: 1, duration: FIRST_IN_SECONDS, ease: "power1.out" },
    CAMERA_SECONDS,
  );
  // the others start hidden under the first one (only once that one is
  // solid), then slide down to their own rows, one after the other
  for (const index of [1, 2]) {
    const start = FAN_START + (index - 1) * FAN_STAGGER;
    const card = `[data-zoom=screen-${index}]`;
    timeline.fromTo(card, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.01 }, start);
    timeline.fromTo(
      card,
      { y: -index * layout.rowPitch, scale: 0.92 },
      { y: 0, scale: 1, duration: FAN_SECONDS, ease: "power3.out" },
      start,
    );
  }
  // each number and label comes in right as its screen lands
  for (const index of [0, 1, 2]) {
    timeline.fromTo(
      `[data-zoom=label-${index}]`,
      { autoAlpha: 0, x: -24 },
      { autoAlpha: 1, x: 0, duration: LABEL_SECONDS, ease: "power2.out" },
      labelStart(index),
    );
  }
  if (layout.note) {
    timeline.fromTo(
      "[data-zoom=note]",
      { autoAlpha: 0, x: 24 },
      { autoAlpha: 1, x: 0, duration: LABEL_SECONDS, ease: "power2.out" },
      labelStart(SCREEN_STEPS.length - 1),
    );
  }
  return timeline;
}

function Machine({ view, capacity }: { view: WasherView; capacity: number }) {
  const doorLoop = view === "door";
  return (
    <div
      className="absolute"
      style={{
        ...box(MACHINE),
        animation: doorLoop ? loop("washer-shake", DOOR_LOOP_SECONDS) : "none",
      }}
    >
      {/* plinth */}
      <div className="absolute left-[10px] right-[10px] top-[466px] bottom-0 bg-gradient-to-b from-[#55595d] to-[#3c4044]" />
      {/* brushed steel body */}
      <div
        className="absolute left-0 right-0 top-0 h-[470px] rounded-[6px] border border-[#5e6266]"
        style={{
          background:
            "repeating-linear-gradient(90deg, rgba(255,255,255,0.035) 0 1px, transparent 1px 3px), linear-gradient(90deg, #74787c 0%, #a2a6a9 22%, #8d9195 50%, #a6aaad 78%, #73777b 100%)",
          perspective: "1100px",
        }}
      >
        <div className="absolute inset-x-2 top-[6px] h-px bg-black/25" />
        <Logo />
        <Touchscreen />
        <div className="absolute left-[86px] top-[112px] w-[56px] h-[18px] rounded-sm bg-[#2a7fa8] flex items-center justify-end pr-[3px]">
          <span className="size-[13px] bg-white" />
        </div>
        <InfoSticker capacity={capacity} />
        <ControlStrip />
        <Gasket />
        <Drum spinning={view === "running"} />
        <Door animate={doorLoop} />
        {doorLoop && <Bang />}
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div className="absolute left-[66px] top-[12px] w-[104px] h-[22px] rounded-sm bg-white/95 flex items-center justify-center">
      <span className="text-[11px] font-black tracking-[0.15em] text-[#b5434a]">
        RENZACCI
      </span>
    </div>
  );
}

function InfoSticker({ capacity }: { capacity: number }) {
  return (
    <div className="absolute left-[192px] top-[10px] w-[290px] h-[108px] rounded-[10px] bg-gradient-to-r from-[#1f6f93] to-[#2a86ad] border border-[#185a77] flex items-center gap-3 px-3">
      <div className="size-[66px] shrink-0 rounded-full border-[5px] border-white/90 bg-[#1b5f80] flex items-center justify-center text-white text-[16px] font-bold">
        {capacity}kg
      </div>
      <div className="flex-1 flex flex-col gap-[5px]">
        {[90, 70, 84, 60, 76, 50].map((width, index) => (
          <span
            key={index}
            className="h-[4px] rounded bg-white/70"
            style={{ width: `${width}%` }}
          />
        ))}
      </div>
      <div className="flex flex-col gap-1 items-center">
        <div className="flex gap-1">
          <span className="size-[26px] bg-white" />
          <span className="size-[26px] bg-white" />
        </div>
        <span className="w-[56px] h-[6px] rounded bg-[#f08a4b]" />
      </div>
    </div>
  );
}

function ControlStrip() {
  return (
    <>
      <div className="absolute left-[372px] top-[132px] w-[92px] h-[22px] rounded-full bg-[#1d4d6b] border border-[#0f2f44] flex items-center justify-around px-2">
        {[0, 1, 2, 3].map((index) => (
          <span key={index} className="size-[8px] rounded-full bg-white/85" />
        ))}
      </div>
      <div className="absolute left-[390px] top-[158px] w-[56px] h-[8px] rounded-sm bg-[#d8443c]" />
    </>
  );
}

function Gasket() {
  return (
    <div
      className="absolute rounded-full"
      style={{
        ...circle(DOOR_CENTER, GASKET_R),
        background: "radial-gradient(circle, #2a2d30 60%, #0c0d0e 100%)",
        boxShadow: "inset 0 0 0 3px #45494d, 0 2px 0 rgba(255,255,255,0.15)",
      }}
    />
  );
}

function Drum({ spinning }: { spinning: boolean }) {
  return (
    <div
      className="absolute rounded-full overflow-hidden"
      style={{
        ...circle(DOOR_CENTER, OPENING_R),
        animation: spinning ? DRUM_SPIN : "none",
        background:
          "radial-gradient(circle, rgba(255,255,255,0.16) 1.5px, transparent 2.5px) 0 0 / 13px 13px, radial-gradient(circle at 50% 40%, #5b6168, #202428 75%)",
        boxShadow: "inset 0 12px 34px rgba(0,0,0,0.75)",
      }}
    >
      <Shirt className="absolute size-28 left-[6%] bottom-[4%] rotate-[-20deg] text-white fill-bub-vivid-blue" />
      <Shirt className="absolute size-24 right-[4%] bottom-[10%] rotate-[25deg] text-white fill-bub-orange" />
      <Shirt className="absolute size-20 left-[34%] bottom-[38%] rotate-[8deg] text-white fill-[#f4c542]" />
    </div>
  );
}

// cuts the glass hole out of the door rings
const ringMask = {
  maskImage: `radial-gradient(circle, transparent ${GLASS_R - 1}px, black ${GLASS_R}px)`,
  WebkitMaskImage: `radial-gradient(circle, transparent ${GLASS_R - 1}px, black ${GLASS_R}px)`,
} satisfies CSSProperties;

// Hinged on the right, like our machines; rotateY(105deg) swings the left
// edge toward the viewer.
function Door({ animate }: { animate: boolean }) {
  const size = DOOR_R * 2;
  return (
    <div
      className="absolute"
      style={{
        ...circle(DOOR_CENTER, DOOR_R),
        transformStyle: "preserve-3d",
        transformOrigin: "100% 50%",
        animation: animate ? loop("washer-door", DOOR_LOOP_SECONDS) : "none",
      }}
    >
      {/* back + middle rings give the door some thickness */}
      <DoorLayer z={0} style={{ background: "#3f4347", ...ringMask }} />
      <DoorLayer z={7} style={{ background: "#666b70", ...ringMask }} />
      {/* brushed chrome front ring */}
      <DoorLayer
        z={14}
        style={{
          background:
            "conic-gradient(from 210deg, #e9ecee, #8b9095, #dfe2e4, #7a7f84, #d4d7da, #959a9f, #e9ecee)",
          ...ringMask,
        }}
      />
      {/* black rubber ring around the glass */}
      <div
        className="absolute rounded-full border-[12px] border-[#151719]"
        style={{
          inset: DOOR_R - GLASS_R - 12,
          transform: "translateZ(15px)",
        }}
      />
      {/* smoky glass, the clothes show through */}
      <div
        className="absolute rounded-full"
        style={{
          inset: DOOR_R - GLASS_R,
          transform: "translateZ(12px)",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.05) 36%, rgba(60,70,80,0.22) 62%, rgba(255,255,255,0.2) 100%)",
        }}
      />
      {/* screws */}
      {[35, 145, 215, 325].map((angle) => (
        <span
          key={angle}
          className="absolute size-[8px] rounded-full bg-[#3a3e42] shadow-[inset_0_1px_1px_rgba(255,255,255,0.5)]"
          style={{
            left: size / 2 + Math.cos((angle * Math.PI) / 180) * 122 - 4,
            top: size / 2 + Math.sin((angle * Math.PI) / 180) * 122 - 4,
            transform: "translateZ(16px)",
          }}
        />
      ))}
    </div>
  );
}

function DoorLayer({ z, style }: { z: number; style: CSSProperties }) {
  return (
    <div
      className="absolute inset-0 rounded-full"
      style={{ transform: `translateZ(${z}px)`, ...style }}
    />
  );
}

// "PUFF!" (en: "BANG!"): the washer's door needs a firm slam
function Bang() {
  return (
    <>
      <div
        className="absolute rounded-full border-[6px] border-white pointer-events-none"
        style={{
          ...circle(DOOR_CENTER, GASKET_R),
          animation: loop("washer-ripple", DOOR_LOOP_SECONDS),
        }}
      />
      <span
        className="absolute right-[372px] top-[180px] rounded-xl bg-bub-orange px-5 py-3 text-white font-fancy font-bold text-[52px] leading-none whitespace-nowrap shadow-lg pointer-events-none"
        style={{ animation: loop("washer-bang", DOOR_LOOP_SECONDS) }}
      >
        <Trans>PUFF!</Trans>
      </span>
    </>
  );
}

// ---- touchscreen ----

function Touchscreen() {
  return (
    <div
      className="absolute rounded-[3px] bg-[#d9dcdd] shadow-[inset_0_-1px_0_rgba(0,0,0,0.2)]"
      style={{
        left: SCREEN.left - 6,
        top: SCREEN.top - 6,
        width: SCREEN.width + 12,
        height: SCREEN.height + 12,
      }}
    >
      <TouchscreenGlass left={6} top={6} />
    </div>
  );
}

function TouchscreenGlass({ left, top }: { left: number; top: number }) {
  return (
    <div
      className="@container absolute overflow-hidden bg-[#eaf3fb] outline outline-[1.5px] outline-[#24282d]"
      style={{ left, top, width: SCREEN.width, height: SCREEN.height }}
    >
      <SelectProgramScreen showTap={false} />
    </div>
  );
}

// the touchscreen's states in order, shown all at once so the order is clear
const SCREEN_STEPS = [
  {
    label: <Trans>Érintsd meg</Trans>,
    screen: <SelectProgramScreen showTap />,
  },
  {
    label: <Trans>Válassz programot</Trans>,
    screen: <ProgramListScreen />,
  },
  {
    label: <Trans>Ezt kell látnod</Trans>,
    screen: <InsertCreditScreen />,
  },
];
// the real touchscreen's shape
const STEP_SCREEN_RATIO = SCREEN.width / SCREEN.height;
const STEP_GAP = 24;
const STEP_BEZEL = 8;
const STEP_LABEL_WIDTH = 280;
const STEP_LABEL_GAP = 32;
const STEP_MAX_SCREEN_HEIGHT = 320;
const NOTE_GAP = 48;
const NOTE_WIDTH = 360;

// after the zoom: the first screen's bezel and finger come in, then the
// others fan out from behind it, one after the other
const FIRST_IN_SECONDS = 0.25;
const FAN_START = CAMERA_SECONDS + FIRST_IN_SECONDS;
const FAN_SECONDS = 0.6;
const FAN_STAGGER = 0.15;
const LABEL_SECONDS = 0.4;

// When the screen steps' labels come in: each right as its screen lands.
function labelStart(index: number) {
  return index === 0
    ? FAN_START
    : FAN_START + (index - 1) * FAN_STAGGER + FAN_SECONDS * 0.6;
}

// Where the steps go, in stage coordinates. Each row gets a third of the
// room's height (plus what's free above it), so the screens grow with the
// kiosk's height. The camera needs
// the first screen's glass, so this is worked out here instead of by flexbox.
// A note goes to the right of the screens; the screens narrow so both fit.
function stepsLayout(
  room: { width: number; height: number },
  freeAbove: number,
  hasNote: boolean,
) {
  const height = room.height + freeAbove;
  const noteWidth = hasNote ? NOTE_GAP + NOTE_WIDTH : 0;
  const screenHeight = Math.min(
    (height - 2 * STEP_GAP) / 3 - 2 * STEP_BEZEL,
    STEP_MAX_SCREEN_HEIGHT,
    (room.width - STEP_LABEL_WIDTH - STEP_LABEL_GAP - 2 * STEP_BEZEL - noteWidth) /
      STEP_SCREEN_RATIO,
  );
  const screenWidth = screenHeight * STEP_SCREEN_RATIO;
  const rowHeight = screenHeight + 2 * STEP_BEZEL;
  const rowWidth =
    STEP_LABEL_WIDTH + STEP_LABEL_GAP + screenWidth + 2 * STEP_BEZEL;
  const left = (STAGE.width - rowWidth - noteWidth) / 2;
  const rowsHeight =
    SCREEN_STEPS.length * rowHeight + (SCREEN_STEPS.length - 1) * STEP_GAP;
  const top = -freeAbove + (height - rowsHeight) / 2;
  const cardLeft = left + STEP_LABEL_WIDTH + STEP_LABEL_GAP;
  return {
    left,
    top,
    cardLeft,
    rowHeight,
    rowPitch: rowHeight + STEP_GAP,
    note: hasNote
      ? {
          left: left + rowWidth + NOTE_GAP,
          top,
          width: NOTE_WIDTH,
          height: rowsHeight,
        }
      : null,
    glass: {
      left: cardLeft + STEP_BEZEL,
      top: top + STEP_BEZEL,
      width: screenWidth,
      height: screenHeight,
    },
  };
}

// Takes over the room once the camera reaches the touchscreen; the zoom
// timeline brings each part in.
function ScreenSteps({
  layout,
  note,
  hidden,
}: {
  layout: StepsLayout;
  note: ReactNode;
  hidden: boolean;
}) {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden={hidden}>
      {layout.note && (
        <div
          data-zoom="note"
          className="absolute flex items-center"
          style={layout.note}
        >
          {/* a tall, narrow column: a filled box would read as a wall of orange */}
          <div className="flex flex-col gap-4 border-l-[6px] border-bub-orange pl-7 text-left">
            <TriangleAlert className="size-12" stroke="#f45e22" />
            <p className="text-[28px] leading-snug text-bub-dark-blue">{note}</p>
          </div>
        </div>
      )}
      {SCREEN_STEPS.map(({ label }, index) => (
        <div
          key={index}
          data-zoom={`label-${index}`}
          className="absolute flex items-center"
          style={{
            left: layout.left,
            top: layout.top + index * layout.rowPitch,
            width: STEP_LABEL_WIDTH,
            height: layout.rowHeight,
          }}
        >
          {/* joins this number to the next one */}
          {index < SCREEN_STEPS.length - 1 && (
            <span
              className="absolute left-[21px] top-1/2 w-[4px] bg-bub-orange/40"
              style={{ height: layout.rowPitch }}
            />
          )}
          <div className="relative flex items-center gap-4 text-left">
            <span className="size-[46px] shrink-0 rounded-full bg-bub-orange flex items-center justify-center font-fancy font-bold text-[28px] leading-none text-white">
              {index + 1}
            </span>
            <span className="text-[28px] font-bold leading-tight">{label}</span>
          </div>
        </div>
      ))}
      {SCREEN_STEPS.map(({ screen }, index) => (
        <div
          key={index}
          data-zoom={`screen-${index}`}
          className="absolute rounded-[10px] bg-[#d9dcdd] shadow-lg"
          style={{
            left: layout.cardLeft,
            top: layout.top + index * layout.rowPitch,
            padding: STEP_BEZEL,
            // the first screen stays on top, the others slide out from under it
            zIndex: SCREEN_STEPS.length - index,
          }}
        >
          <div
            className="@container relative overflow-hidden bg-[#eaf3fb] outline outline-[2px] outline-[#24282d]"
            style={{
              width: layout.glass.width,
              height: layout.glass.height,
            }}
          >
            {screen}
          </div>
        </div>
      ))}
    </div>
  );
}

function SelectProgramScreen({ showTap }: { showTap: boolean }) {
  return (
    <>
      <p className="absolute top-[9%] w-full text-center text-[#b5434a] font-black tracking-[0.2em] text-[7cqw]">
        RENZACCI
      </p>
      <div className="absolute left-[12%] right-[12%] top-[38%] bottom-[20%] rounded-[1.5cqw] bg-gradient-to-br from-[#6c63c9] to-[#8f8be0] flex items-center justify-center">
        <span className="text-white font-bold text-[7cqw] whitespace-nowrap">
          SELECT A PROGRAM
        </span>
      </div>
      {showTap && <ScreenTap left="60%" top="62%" />}
    </>
  );
}

const PROGRAMS = [
  "1. PAMUT/COTTON-60°",
  "4. KENYES/DELI-30°",
  "2. SZINES/COLOR-40°",
  "5. AGYNEM/DUVET-30°",
  "3. FEHER/WHITE-40°",
  "6. SZENNYEZETT-60°",
];

function ProgramListScreen() {
  return (
    <>
      <div className="absolute inset-[4%] grid grid-cols-2 grid-rows-[1fr_1fr_1fr_0.7fr] gap-[1.2cqw]">
        {PROGRAMS.map((program) => (
          <div
            key={program}
            className={cn(
              "rounded-[0.6cqw] bg-[#6c63c9] text-white font-bold flex items-center px-[2cqw] text-[3.6cqw] leading-tight",
              program.startsWith("2.") &&
                "outline outline-[0.8cqw] outline-bub-orange",
            )}
          >
            {program}
          </div>
        ))}
        <div className="rounded-[0.6cqw] bg-[#f0a070] text-white/90 font-bold flex items-center justify-center text-[3.4cqw]">
          BACK
        </div>
        <div className="rounded-[0.6cqw] bg-[#f0a070] text-white/90 font-bold flex items-center justify-center text-[3.4cqw]">
          OTHER PROGRAMS
        </div>
      </div>
      <ScreenTap left="30%" top="42%" />
    </>
  );
}

// After picking a program the machine waits for the payment here; it goes
// back to SELECT A PROGRAM if nobody pays for 2 minutes.
function InsertCreditScreen() {
  return (
    <div className="absolute inset-0 bg-[#e6e6f4] text-[#3a3a3a]">
      {/* blue band: the chosen program, then credit / time / temperature */}
      <div className="absolute inset-x-0 top-0 h-[50%] bg-[#2a3ad8]">
        <p className="h-[30%] flex items-center justify-center text-white font-bold text-[5cqw] whitespace-nowrap">
          2. SZINES/COLOR-40°
        </p>
        <div className="absolute left-[3%] right-[3%] top-[30%] bottom-[8%] bg-[#e6e6f4] flex items-center px-[4%]">
          {/* what the user should see: marked like the dryer's temperature */}
          <span className="w-[48%] h-[48%] bg-[#d6d45a] flex items-center justify-center text-[4.6cqw] font-semibold whitespace-nowrap outline outline-[0.8cqw] outline-offset-[0.8cqw] outline-bub-orange animate-[washer-mark_1.2s_ease-in-out_infinite]">
            Insert Credit
          </span>
          <span className="ml-auto flex flex-col items-center gap-[1cqw] text-[3.6cqw] leading-none font-semibold">
            <Clock style={{ width: "7cqw", height: "7cqw" }} strokeWidth={1.6} />
            00:29
          </span>
          <span className="ml-[5%] flex flex-col items-center gap-[1cqw] text-[3.6cqw] leading-none font-semibold">
            <Thermometer style={{ width: "7cqw", height: "7cqw" }} strokeWidth={1.6} />
            40°C
          </span>
        </div>
      </div>
      <div className="absolute left-[3%] bottom-[2%] h-[16%] w-[34%] flex">
        <span
          className="h-full w-[24%] bg-[#2f3c66]"
          style={{ clipPath: "polygon(0 50%, 100% 0, 100% 100%)" }}
        />
        <span className="flex-1 bg-[#ef7b5c] flex items-center justify-center text-[#3a3f66] font-semibold text-[4.4cqw]">
          BACK
        </span>
      </div>
    </div>
  );
}

// Fingertip sits at (left, top). Sized in cqw so it scales with the zoom.
function ScreenTap({ left, top }: { left: string; top: string }) {
  return (
    <span className="absolute size-0 pointer-events-none" style={{ left, top }}>
      <span className="absolute -left-[5cqw] -top-[5cqw] size-[10cqw] rounded-full bg-bub-orange/60 animate-ping" />
      <TapHand className="absolute -left-[4.9cqw] -top-[1.35cqw] size-[13cqw] drop-shadow" />
    </span>
  );
}

// One closed outline so the white fill is solid (lucide's Pointer is open
// strokes, so the background shows through). Fingertip is at (9, 2.5).
export function TapHand({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path
        d="M7.5 4a1.5 1.5 0 0 1 3 0v6.5a1.4 1.4 0 0 1 2.8 0v.7a1.4 1.4 0 0 1 2.8 0v.8a1.4 1.4 0 0 1 2.8 0v4.5c0 3-1.9 5.5-5.4 5.5h-2c-2.5 0-3.7-1.2-4.9-3l-2.4-3.8a1.3 1.3 0 0 1 2-1.6L7.5 15Z"
        fill="white"
        stroke="#f45e22"
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---- helpers ----

function box({
  left,
  top,
  width,
  height,
}: {
  left: number;
  top: number;
  width: number;
  height: number;
}) {
  return { left, top, width, height };
}

function circle(center: { x: number; y: number }, radius: number) {
  return {
    left: center.x - radius,
    top: center.y - radius,
    width: radius * 2,
    height: radius * 2,
  };
}

function loop(name: string, seconds: number) {
  return `${name} ${seconds}s infinite`;
}

// Door loop (5 s): open 0–12% → slams shut 12–40% → bounce, shake + PUFF
// 40–52% → stays closed → swings open again 86–100%.
function WasherKeyframes() {
  return (
    <style>{`
      @keyframes washer-drum-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(1800deg); }
      }
      @keyframes washer-drum-steady {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes washer-door {
        0%, 12% { transform: rotateY(105deg); animation-timing-function: cubic-bezier(.55,0,.9,.55); }
        40% { transform: rotateY(0deg); animation-timing-function: ease-out; }
        44% { transform: rotateY(5deg); animation-timing-function: ease-in; }
        48%, 86% { transform: rotateY(0deg); animation-timing-function: ease-in-out; }
        100% { transform: rotateY(105deg); }
      }
      @keyframes washer-shake {
        0%, 40%, 53%, 100% { transform: none; }
        41% { transform: translate(10px, -3px) rotate(1.2deg); }
        43% { transform: translate(-9px, 2px) rotate(-1deg); }
        45% { transform: translate(7px, -2px) rotate(.7deg); }
        47% { transform: translate(-5px, 1px) rotate(-.4deg); }
        49% { transform: translate(3px, 0) rotate(.2deg); }
        51% { transform: translate(-1px, 0); }
      }
      @keyframes washer-ripple {
        0%, 40% { opacity: 0; transform: scale(1); }
        41% { opacity: .9; transform: scale(1); }
        55%, 100% { opacity: 0; transform: scale(1.3); }
      }
      @keyframes washer-bang {
        0%, 40% { opacity: 0; transform: rotate(10deg) scale(.3); }
        43% { opacity: 1; transform: rotate(10deg) scale(1.35); }
        46% { transform: rotate(6deg) scale(.95); }
        49%, 74% { opacity: 1; transform: rotate(10deg) scale(1); }
        80%, 100% { opacity: 0; transform: rotate(10deg) scale(1); }
      }
      @keyframes washer-mark {
        0%, 100% { outline-color: rgb(244 94 34 / .35); }
        50% { outline-color: rgb(244 94 34 / 1); }
      }
    `}</style>
  );
}
